import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { ACTIVITY_TYPES } from 'constants/general';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { TabBodyWrapper } from '../TabBodyWrapper';
import { ActivityType } from 'interfaces/common.interface';
import { isActivityFilterByAll } from 'utils/validators';
import {
  morningActivitiesSelector,
  addNewActivityButtonSelector
} from 'store/reducer/setting/selectors';
import { changeActivityOrder, newHabit } from 'store/reducer/setting/slice';
import { updateFilterIsSettingToolbarShow } from 'store/reducer/filter/slice';
import { getNewHabitProps } from 'utils/helpers';
import RoutineActivitiesWrapperRevamp from './RoutineActivitiesWrapperRevamp';
import DraggableContentRevamp from '../DraggableContentRevamp';
import AddHabitButton from 'components/setting-generator-revamp/tabs-revamp/AddHabitButton';
import { t } from 'services/i18n.service';
import TabBodyHeaderRevamp from './tab-body-header-revamp';
import SkeletonRoutineList from '../skeleton-routine';

export default function MorningRevamp() {
  const dispatch = useAppDispatch();
  const {
    morning_activities,
    filter: { searchWord, daysOfWeek, priority },
    isSettingLoading
  } = useAppSelectorShallow(morningActivitiesSelector);

  const {
    tabs,
    activeTabIndex,
    customRoutine,
    currentSettings,
    routineSuggestionsSelectedRoutines,
    platform
  } = useAppSelectorShallow(addNewActivityButtonSelector);

  const isSettingToolbarShown = useAppSelectorShallow(
    (state) => state.filter.isSettingToolbarShown
  );

  const sequence_id = morning_activities?.[0]?.activity_sequence_id ?? uuid();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const visibleActivities = (morning_activities ?? []).filter((activity) =>
    isActivityFilterByAll(activity, searchWord, priority, daysOfWeek)
  );

  const visibleItemIds = visibleActivities.map((a) => String(a.id));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const fullIds = (morning_activities ?? []).map((a) => String(a.id));
      const oldIndex = fullIds.indexOf(String(active.id));
      const newIndex = fullIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      const reordered = arrayMove(
        Array.from(morning_activities ?? []),
        oldIndex,
        newIndex
      );
      dispatch(
        changeActivityOrder({
          type: ACTIVITY_TYPES.MORNING,
          data: reordered
        })
      );
    },
    [morning_activities, dispatch]
  );

  const handleAddHabit = () => {
    const [activity_type, sequence_id] = getNewHabitProps(
      tabs,
      activeTabIndex,
      currentSettings,
      platform,
      customRoutine,
      routineSuggestionsSelectedRoutines
    );
    dispatch(
      newHabit({
        type: activity_type,
        sequence_id
      })
    );
  };

  const handleFilter = () => {
    dispatch(updateFilterIsSettingToolbarShow(!isSettingToolbarShown));
  };

  if (isSettingLoading) return <SkeletonRoutineList />;

  return (
    <TabBodyWrapper>
      <TabBodyHeaderRevamp
        onAddHabit={handleAddHabit}
        onFilter={handleFilter}
      />
      {morning_activities?.length ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <RoutineActivitiesWrapperRevamp>
            <SortableContext
              items={visibleItemIds}
              strategy={verticalListSortingStrategy}>
              {morning_activities?.map(
                (activity: ActivityType, index: number) => {
                  if (
                    isActivityFilterByAll(
                      activity,
                      searchWord,
                      priority,
                      daysOfWeek
                    )
                  ) {
                    return (
                      <DraggableContentRevamp
                        key={activity.id}
                        id={String(activity.id)}
                        position={index}
                        type={ACTIVITY_TYPES.MORNING}
                        sequence_id={sequence_id}
                      />
                    );
                  }
                  return null;
                }
              )}
            </SortableContext>
          </RoutineActivitiesWrapperRevamp>
        </DndContext>
      ) : null}
      {!morning_activities?.length && (
        <AddHabitButton routine={t('morning_routine')} />
      )}
    </TabBodyWrapper>
  );
}
